import { useEffect, useState } from 'react';
import { FileText, Printer } from 'lucide-react';
import { getMyIssuedReport, type IssuedReportDto } from '../learnerPlanApi';

// The student's own entry point to the progress report their instructor
// issued — the "icon on their dash board so they can click, view and print"
// from the request.
//
// IT RENDERS NOTHING UNTIL A REPORT HAS ACTUALLY BEEN ISSUED. An entry point
// with nothing behind it is worse than no entry point: a student who clicks it
// and lands on "nothing here" learns to ignore it, and will still be ignoring
// it in week ten when there IS something. The backend answers 204 for "not
// issued yet" precisely so this can tell the two apart without treating a
// normal state as an error.
//
// The link opens the existing chrome-free printable route with `?issued=latest`
// — the same page, the same progressReport.css, the same @media print block.
// There is deliberately no second print path for students: a separate one would
// be the copy that quietly stops matching the instructor's.
//
// A failure to load is silent by design. This card is an extra on a dashboard
// whose job is to show a course; a red error where an optional link would have
// been is noise the student can do nothing about.

function fmtDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'recently';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function IssuedReportCard() {
  const [issued, setIssued] = useState<IssuedReportDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyIssuedReport()
      .then((r) => { if (!cancelled) setIssued(r); })
      .catch(() => { if (!cancelled) setIssued(null); });
    return () => { cancelled = true; };
  }, []);

  if (!issued) return null;

  const { report } = issued;
  const openable = `/reports/learner-plan/me?issued=latest`;

  return (
    <a
      href={openable}
      target="_blank"
      rel="noopener"
      data-testid="issued-report-card"
      className="ws-tap group mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-ws-border bg-ws-surface p-4 shadow-sm hover:border-ws-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-ws-accent-quiet text-ws-accent">
        <FileText size={21} />
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-sm font-semibold text-ws-ink">Your progress report</span>
        <span className="block text-[12px] text-ws-ink-3">
          Issued {fmtDate(issued.issued_at)} · {report.plan.counts.complete} of{' '}
          {report.plan.counts.total} plan items complete
          {report.plan.counts.overdue > 0
            ? ` · ${report.plan.counts.overdue} past the due date`
            : ''}
        </span>
        <span className="mt-0.5 block text-[11px] text-ws-ink-4">
          A snapshot of where things stood on that date — it does not change afterwards.
        </span>
      </span>
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-ws-border px-2.5 py-1.5 text-[12px] font-medium text-ws-ink-2 group-hover:border-ws-accent group-hover:text-ws-accent-ink">
        <Printer size={13} /> View &amp; print
      </span>
    </a>
  );
}
